/**
 * @file Composable for delaying post sorting after vote/rating/score property responses
 *
 * When a user votes/rates/scores on a board sorted by that custom property:
 * - On non-touchable devices: we defer the re-sort until they hover out to avoid jarring UX on desktop
 * - On touchable devices: we defer the re-sort for 1 second after the response
 *
 * A frozen copy of the post's custom property value is stored and passed to
 * sortPostsByCustomProp as an override, keeping the post in place until the delay expires.
 *
 * Screen reader announcements are generated when sorting completes, providing specific position changes
 * in the format: "Post title moved up 3 places. New position: 2."
 *
 * State is shared at module level so all consumers (useSurfaceCustomPropertyResponse,
 * surface_post_sorter, SurfacePostBeingShowed) share the same tracking state.
 *
 * Assumes that post never changes section.
 */
import device from '@@/bits/device'
import { np__ } from '@@/bits/intl'
import { isSortingByCustomProp } from '@@/bits/surface_settings_helper'
import { useScreenReaderNotificationsStore } from '@@/pinia/screen_reader_notifications'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useSurfaceWishArrangementStore } from '@@/pinia/surface_wish_arrangement'
import type { Id, PostCustomPropertyValue } from '@@/types'
import { computed, ref } from 'vue'

const TOUCHABLE_DELAY_MS = 1000

// Module-level shared state — singleton across all consumers
const trackedPostId = ref<Id | null>(null)
const frozenValue = ref<PostCustomPropertyValue>(null)
const oldPosition = ref<number | null>(null)
let delayTimer: ReturnType<typeof setTimeout> | null = null

function useDelayedCustomPropertySorting() {
  const surfaceWishArrangementStore = useSurfaceWishArrangementStore()
  const surfacePostsStore = useSurfacePostsStore()
  const screenReaderNotificationStore = useScreenReaderNotificationsStore()

  const computePosition = (postId: Id): number | null => {
    // Not grouped
    if (surfaceWishArrangementStore.isGroupByNone) {
      const position = surfacePostsStore.allSortedPosts.findIndex((post) => post.id === postId)
      return position >= 0 ? position : null
    }

    // Grouped
    for (const key in surfacePostsStore.allSortedPostsByGroup) {
      const posts = surfacePostsStore.allSortedPostsByGroup[key]
      const position = posts?.findIndex((post) => post.id === postId) ?? -1
      if (position >= 0) return position
    }

    // Not found
    return null
  }

  const announcePositionChangeOnScreenReader = ({
    postSubject,
    oldPosition: oldPos,
    newPosition,
  }: {
    postSubject: string | undefined | null
    oldPosition: number | null
    newPosition: number | null
  }) => {
    if (oldPos == null || newPosition == null || oldPos === newPosition) return

    let message: string
    const intlContext =
      'Posts are reordered when user votes on it via custom post properties (e.g. votes, star rating, score).'
    const currentPosition = newPosition + 1 // 1-based position

    if (newPosition > oldPos) {
      const numPlaces = newPosition - oldPos
      const params = { postSubject, numPlaces, currentPosition }
      message =
        postSubject == null
          ? np__(
              intlContext,
              'The post you responded to moved down %{numPlaces} place. New position: %{currentPosition}.',
              'The post you responded to moved down %{numPlaces} places. New position: %{currentPosition}.',
              numPlaces,
              params,
            )
          : np__(
              intlContext,
              '"%{postSubject}" moved down %{numPlaces} place. New position: %{currentPosition}.',
              '"%{postSubject}" moved down %{numPlaces} places. New position: %{currentPosition}.',
              numPlaces,
              params,
            )
    } else {
      const numPlaces = oldPos - newPosition
      const params = { postSubject, numPlaces, currentPosition }
      message =
        postSubject == null
          ? np__(
              intlContext,
              'The post you responded to moved up %{numPlaces} place. New position: %{currentPosition}.',
              'The post you responded to moved up %{numPlaces} places. New position: %{currentPosition}.',
              numPlaces,
              params,
            )
          : np__(
              intlContext,
              '"%{postSubject}" moved up %{numPlaces} place. New position: %{currentPosition}.',
              '"%{postSubject}" moved up %{numPlaces} places. New position: %{currentPosition}.',
              numPlaces,
              params,
            )
    }
    screenReaderNotificationStore.addScreenReaderMessage(message)
  }

  const clearDelayTimer = (): void => {
    if (delayTimer != null) {
      clearTimeout(delayTimer)
      delayTimer = null
    }
  }

  /**
   * Check if the board is sorted by the given custom property.
   * Used to decide whether to defer re-sorting after a response update.
   */
  const isSortedByProperty = (customPropertyId: string): boolean => {
    const wishSortBy = surfaceWishArrangementStore.originalWishSortBy
    if (!isSortingByCustomProp(wishSortBy)) return false
    const [, sortedPropertyId] = wishSortBy.split(':')
    return sortedPropertyId === customPropertyId
  }

  const startTrackingPostForDelayedCustomPropSort = (postId: Id, frozenPropValue: PostCustomPropertyValue): void => {
    clearDelayTimer()

    trackedPostId.value = postId
    frozenValue.value = frozenPropValue
    oldPosition.value = computePosition(postId)

    // On touchable devices, apply sorting after a delay since there's no mouseleave.
    // On non-touchable devices, mouseleave on the post triggers sorting (via SurfacePostBeingShowed).
    if (device.touchable) {
      delayTimer = setTimeout(() => {
        stopTrackingPostForDelayedCustomPropSort(postId)
      }, TOUCHABLE_DELAY_MS)
    }
  }

  const stopTrackingPostForDelayedCustomPropSort = (postId: Id): void => {
    if (trackedPostId.value !== postId) return

    clearDelayTimer()
    trackedPostId.value = null
    frozenValue.value = null

    // Announce after the new positions take effect
    announcePositionChangeOnScreenReader({
      oldPosition: oldPosition.value,
      newPosition: computePosition(postId),
      postSubject: surfacePostsStore.postEntitiesById[postId]?.subject,
    })

    // Clear oldPosition only after the screen reader message has been populated
    oldPosition.value = null
  }

  /**
   * When a post is being tracked for delayed custom property sorting, this provides
   * the frozen (pre-update) value that the sorter should use instead of the live value.
   */
  const customPropertyOverrideForSorting = computed<{ postId: Id; value: PostCustomPropertyValue } | null>(() => {
    if (trackedPostId.value == null) {
      return null
    }
    return {
      postId: trackedPostId.value,
      value: frozenValue.value,
    }
  })

  return {
    isSortedByProperty,
    customPropertyOverrideForSorting,
    startTrackingPostForDelayedCustomPropSort,
    stopTrackingPostForDelayedCustomPropSort,
  }
}
export { useDelayedCustomPropertySorting }
